import { semImagem } from 'assets/svg'
import { MessageCircle } from 'lucide-react'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { VscPackage } from 'react-icons/vsc'
import { useHistory, useLocation } from 'react-router'
import { InputSearch } from '../../components/InputSearch'
import { Badge } from '../../new-components/atoms/Badge'
import OrderHelpPanel from '../../components/SupportChat/OrderHelpPanel'
import { useMyConversationsUnread } from '../../services/resources/supportChat'
import Table, { ColDef } from '../../new-components/atoms/Table'
import api from '../../services/api'
import { formatIsoDateToDisplay } from '../../utils/formatters'
import OrderCardMobile from './components/OrderCardMobile'
import OrderStatusCell from './components/OrderStatusCell'
import { OrdersContainer } from './styles'
import { UserOrder, UserOrderResponse } from './types'

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
  const image = event.currentTarget
  image.src = semImagem
}

const OrderNumberCell = ({ row }: { row: UserOrder }) => (
  <div className="flex items-center gap-3">
    <img
      src={row.items_summary?.first_product_image || semImagem}
      alt=""
      loading="lazy"
      onError={handleImageError}
      className="h-10 w-10 shrink-0 rounded-full border border-solid border-gray-200 bg-white object-contain p-1"
    />

    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-semibold text-black">#{row.id}</span>
        {row.is_weight_loss_program && (
          <Badge variant="primary_light" size="xs">
            Programa Perda de Peso
          </Badge>
        )}
      </div>
      {row.items_summary?.first_product_name && (
        <span className="truncate text-xs text-gray-500" title={row.items_summary.first_product_name}>
          {row.items_summary.first_product_name}
        </span>
      )}
      {!!row.items_summary?.count && (
        <span className="text-xs text-gray-500">
          {row.items_summary.count} {row.items_summary.count === 1 ? 'item' : 'itens'}
        </span>
      )}
    </div>
  </div>
)

const OrderDateCell = ({ row }: { row: UserOrder }) => (
  <span className="text-sm text-black">{formatIsoDateToDisplay(row.payments?.[0]?.createdAt ?? row.createdAt)}</span>
)

const OrderAmountCell = ({ row }: { row: UserOrder }) => (
  <span className="text-sm text-black">{currency.format(Number(row.amount) || 0)}</span>
)

const StatusCell = ({ row }: { row: UserOrder }) => <OrderStatusCell status={row.status} />

/**
 * "Pedir ajuda" escreve o pedido na query string (?ajuda=<id>) em vez de usar callback,
 * seguindo o padrão de painéis do projeto. A Frente 3 lê esse parâmetro para abrir o
 * chat já com o pedido em contexto — e o botão voltar do navegador fecha o painel.
 */
const OrderActionsCell = ({ row }: { row: UserOrder }) => {
  const history = useHistory()
  // Mesma query para todas as linhas: o cache faz uma chamada só.
  const { data: unreadByOrder } = useMyConversationsUnread()
  const unread = unreadByOrder?.[Number(row.id)] ?? 0

  return (
    <div className="flex items-center justify-center gap-2 whitespace-nowrap">
      <button
        type="button"
        onClick={() => history.push(`?pagina=pedidos-todos&ajuda=${row.id}`)}
        className={`relative flex items-center gap-1.5 rounded-md border-solid px-3 py-1.5 text-xs transition ${
          unread > 0
            ? 'border border-green-700 bg-green-50 font-semibold text-green-800 hover:bg-green-100'
            : 'border-[0.5px] border-green-700 bg-transparent font-normal text-green-700 hover:bg-green-50'
        }`}>
        <MessageCircle size={14} />
        {unread > 0 ? 'Nova mensagem' : 'Pedir ajuda'}
        {/* Mensagens da equipe que o paciente ainda não abriu. */}
        {unread > 0 && (
          <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-green-700 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={() => history.push(`/detalhes-do-pedido/${row.id}`)}
        className="rounded-md border-[0.5px] border-solid border-black bg-transparent px-3 py-1.5 text-xs font-normal text-black hover:bg-gray-100">
        Ver detalhes
      </button>
    </div>
  )
}

const colDefs: ColDef<UserOrder>[] = [
  {
    id: 'order',
    label: 'Nº do pedido',
    cellRenderer: OrderNumberCell,
    className: 'w-[260px] max-w-[260px]'
  },
  { id: 'date', label: 'Data da compra', cellRenderer: OrderDateCell, className: 'whitespace-nowrap' },
  { id: 'amount', label: 'Valor do pedido', cellRenderer: OrderAmountCell, className: 'whitespace-nowrap' },
  { id: 'status', label: 'Status', cellRenderer: StatusCell, className: 'w-[180px] whitespace-nowrap' },
  { id: 'actions', label: 'Ações', cellRenderer: OrderActionsCell, className: 'whitespace-nowrap text-center' }
]

function UserOrderList() {
  const history = useHistory()
  const { pathname, search: locationSearch } = useLocation()
  const [search, setSearch] = useState('')
  const [orders, setOrders] = useState<UserOrder[]>([])
  const [loading, setLoading] = useState(false)

  const getOrders = async () => {
    try {
      setLoading(true)
      const { data } = await api.get<UserOrderResponse>('/my-account/my-orders')
      setOrders(data?.orders ?? [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getOrders()
  }, [])

  /**
   * O pedido em atendimento vem da URL (`?ajuda=<id>`), não de um estado local:
   * assim o botão da tabela e o do card mobile abrem o mesmo painel sem precisar
   * de callback, o link pode ser compartilhado e o voltar do navegador fecha.
   */
  const helpOrderId = useMemo(() => {
    const value = new URLSearchParams(locationSearch).get('ajuda')

    return value && /^[0-9]+$/.test(value) ? Number(value) : null
  }, [locationSearch])

  const closeHelp = () => {
    const params = new URLSearchParams(locationSearch)
    params.delete('ajuda')

    // replace, não push: fechar o painel não merece uma entrada no histórico.
    history.replace(`${pathname}?${params.toString()}`)
  }

  /**
   * Troca o pedido em atendimento sem fechar a conversa anterior: ela fica em
   * `bot`, sem NPS, e o paciente pode voltar. Encerrar é outra ação.
   */
  const trocarPedidoDoAtendimento = (id: number) => {
    const params = new URLSearchParams(locationSearch)
    params.set('ajuda', String(id))

    history.replace(`${pathname}?${params.toString()}`)
  }

  /**
   * Até três pedidos para oferecer quando o assunto do atual se encerra.
   *
   * Mesmo critério do backend em `n8nOrdersService` (`...:607`): só pedido
   * **pago** entra, porque pedido sem pagamento não tem o que acompanhar.
   * `lost` fica de fora — a assistente não fala de pedido perdido com o
   * paciente, então oferecê-lo levaria a uma conversa que ela não pode ter.
   */
  const outrosPedidos = useMemo(
    () =>
      orders
        .filter((order) => order.id !== helpOrderId)
        .filter((order) => (order.payments ?? []).some((payment) => payment.status === 'paid'))
        .filter((order) => order.status?.status !== 'lost')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3),
    [orders, helpOrderId]
  )

  const filteredOrders = useMemo(
    () =>
      orders
        .filter((order) => order.id.toString().includes(search) || order.createdAt.toString().includes(search))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [orders, search]
  )

  return (
    <OrdersContainer>
      <div className="order_wrapper">
        <div className="order_title mb-3">
          <VscPackage className="text-black" />
          <span className="ml-2 text-lg font-bold text-black">Meus Pedidos</span>
        </div>

        <div className="order_subtitle">
          <h3 className="mb-3 text-sm font-normal text-black">
            Somente pedidos realizados a partir de <b>01/10/2023</b> serão exibidos nesta área
          </h3>
        </div>

        <div className="order_filter">
          <InputSearch
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            width="85%"
            placeholder="Pesquisar pelo Numero do Pedido"
            className="text-black"
            style={{ width: '100%' }}
          />
          <p>
            {filteredOrders.length} {filteredOrders.length === 1 ? 'compra' : 'compras'}
          </p>
        </div>

        {/* desktop: tabela */}
        <div className="hidden md:block [&_tbody_td]:align-middle [&_thead_th:first-child]:rounded-l-lg [&_thead_th:last-child]:rounded-r-lg [&_thead_th]:bg-gray-100 [&_thead_th]:py-2.5 [&_thead_th]:text-xs [&_thead_th]:font-semibold [&_thead_th]:text-gray-700">
          <Table<UserOrder> colDefs={colDefs} data={filteredOrders} isLoading={loading} />
        </div>

        {/* mobile: cards — tabela com 5 colunas não cabe em tela pequena */}
        <ul className="flex list-none flex-col gap-3 p-0 md:hidden">
          {!loading && filteredOrders.length === 0 && (
            <li className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-500">
              Não localizamos pedidos em seu nome
            </li>
          )}
          {filteredOrders.map((order) => (
            <OrderCardMobile key={order.uuid ?? order.id} order={order} />
          ))}
        </ul>
      </div>

      <OrderHelpPanel
        orderId={helpOrderId}
        onClose={closeHelp}
        outrosPedidos={outrosPedidos}
        onTrocarPedido={trocarPedidoDoAtendimento}
      />
    </OrdersContainer>
  )
}

export default UserOrderList
